import React, { useRef, useState, useCallback } from "react";
import { Box, Typography, Tooltip, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReplayIcon from "@mui/icons-material/Replay";
import { Candidate } from "../../services/preventiviApi";
import { preventiviApi, getCandidateLabel } from "../../services/preventiviApi";
import { MatchTypeChip } from "../common/StatusChip";
import { MatchPickerPopover } from "./MatchPickerPopover";
import { searcherApi } from "../../services/searcherApi";
import toast from "react-hot-toast";

interface MatchPickerCellProps {
  candidates: Candidate[] | null | undefined;
  selectedUid: string | null | undefined;
  queryText: string;
  readonly?: boolean;
  onSelectCandidate: (candidate: Candidate, mergedCandidates: Candidate[]) => void;
  /** Called when the user removes the current selection without replacing it. */
  onDeselectCandidate?: () => void;
  preventivoCategoria?: string | null;
  disciplina?: "ELE" | "MEC";
  preventivoId?: string;
  rowId?: string;
  onRematchStarted?: () => void;
  /** Pipeline error for this row — shown as explicit error state, fix manually. */
  matchError?: string | null;
  /** Codice NP.* — excluded from automatic matching by design, always manual. */
  isNp?: boolean;
}


export const MatchPickerCell: React.FC<MatchPickerCellProps> = ({
  candidates,
  selectedUid,
  queryText,
  readonly,
  onSelectCandidate,
  onDeselectCandidate,
  preventivoCategoria,
  disciplina,
  preventivoId,
  rowId,
  onRematchStarted,
  matchError,
  isNp,
}) => {
  const cellRef = useRef<HTMLDivElement>(null);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [rematching, setRematching] = useState(false);

  // Suggestions are the persisted batch-pipeline candidates — fixed at compute
  // time, never re-fetched. The user can always find new items via the
  // "Ricerca manuale" tab inside the popover.
  const handleOpenPopover = useCallback(() => {
    if (readonly) return;
    setPopoverOpen(true);
  }, [readonly]);

  // Only surface candidates that passed the quality filter.
  // "excluded" or null match_type means the LLM rejected / didn't classify them.
  // manually_selected (new flag) or match_type === "manual" (legacy, pre-flag
  // data saved before manually_selected was introduced) are both treated as positive.
  const PIPELINE_POSITIVE = new Set(["codice", "exact", "acceptable"]);
  const isManual = (c: { match_type?: string | null; manually_selected?: boolean }) =>
    c.manually_selected === true || c.match_type === "manual";
  const isPositive = (c: { match_type?: string | null; manually_selected?: boolean }) =>
    isManual(c) || PIPELINE_POSITIVE.has(c.match_type ?? "");

  const top = (() => {
    if (!candidates?.length) return null;
    // Only show a candidate when selectedUid is explicitly set by the pipeline
    // auto-select or by a user manual pick.  When selectedUid is null/undefined:
    //   a) the pipeline found no positive candidates (all excluded), or
    //   b) the user explicitly deselected without picking another item.
    // In both cases we must NOT auto-pick another item from the list — that
    // was the root cause of the "deselected element still visible" bug.
    if (!selectedUid) return null;
    const sel = candidates.find((c) => c.uid === selectedUid);
    // Show any positively-classified or manually-selected candidate.
    // An excluded/unclassified item set as selectedUid (pre-pipeline old data)
    // is treated as "no valid match" so the user is prompted to re-select.
    if (sel && isPositive(sel)) return sel;
    return null;
  })();

  // Valid-empty states (pipeline completed correctly, no error):
  //  - allExcluded: the selector judged every candidate as incompatible
  //  - noCandidates: retrieval found nothing at all
  // Both require manual search, but they are NOT errors — visually distinct
  // (amber/warning) from matchError (red).
  // Positive suggestions exist but none is selected → the user removed the match.
  const deselected = !matchError && !top && !!candidates?.some(isPositive);
  const allExcluded = !matchError && !top && !deselected && !!candidates?.length;
  const noCandidates = !matchError && !top && !candidates?.length;
  const needsManual = deselected || allExcluded || noCandidates;

  const meta = top?.metadata ?? {};

  const handleDeselect = async () => {
    if (!selectedUid) return;
    try {
      await searcherApi.feedback({ query: queryText, item_uid: selectedUid, signal: -1 });
    } catch {
      // best effort
    }
    onDeselectCandidate?.();
  };

  const handleRematch = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!preventivoId || !rowId || rematching) return;
    setRematching(true);
    try {
      await preventiviApi.rematchRow(preventivoId, rowId);
      toast.success("Rimatch avviato per questa riga");
      onRematchStarted?.();
    } catch {
      toast.error("Errore nel rimatch della riga");
    } finally {
      setRematching(false);
    }
  };

  const handleChoose = async (candidate: Candidate) => {
    const prevUid = selectedUid;
    // Mark as manually chosen (preserving the pipeline-assigned match_type so
    // it still shows correctly in the Suggerimenti list). The cell display uses
    // manually_selected to show the "Manuale" overlay label.
    const manualCandidate: Candidate = { ...candidate, manually_selected: true };
    // Merge into the existing list (replace stale entry if present so metadata
    // is always up to date). Clear manually_selected from all other candidates
    // so that only the current selection carries the "Manuale" marker.
    const existing = (candidates ?? [])
      .filter((c) => c.uid !== candidate.uid)
      .map((c) => (c.manually_selected ? { ...c, manually_selected: false } : c));
    const merged = [manualCandidate, ...existing];
    onSelectCandidate(manualCandidate, merged);

    // Record feedback.
    // Replacement: send signal=-1 with replaced_by only — the backend
    // automatically inserts an inferred_from_replace +1 for the new item,
    // so we must NOT send a second explicit +1 here to avoid duplicates.
    if (prevUid && prevUid !== candidate.uid) {
      try {
        await searcherApi.feedback({ query: queryText, item_uid: prevUid, signal: -1, replaced_by: candidate.uid });
      } catch {
        // best effort
      }
    } else if (!prevUid) {
      try {
        await searcherApi.feedback({ query: queryText, item_uid: candidate.uid, signal: 1 });
      } catch {}
    }
  };

  const statusLabel = (icon: React.ReactNode, label: string, color: string, tooltip: string) => (
    <Tooltip title={tooltip}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color }}>
        {icon}
        <Typography sx={{ color, fontWeight: 700, fontSize: "0.7rem" }}>{label}</Typography>
      </Box>
    </Tooltip>
  );

  return (
    <>
      <Box
        ref={cellRef}
        onClick={handleOpenPopover}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 0.5,
          cursor: readonly ? "default" : "pointer",
          px: 0.75,
          py: 0.4,
          borderRadius: "6px",
          minWidth: 0,
          border: (matchError || needsManual || isNp) ? "1.5px solid" : "1.5px solid transparent",
          borderColor: matchError ? "error.main" : isNp ? "#C9B400" : needsManual ? "#F57C00" : "transparent",
          bgcolor: matchError ? "rgba(211,47,47,0.04)" : isNp ? "rgba(201,180,0,0.08)" : needsManual ? "rgba(245,124,0,0.06)" : undefined,
          "&:hover": readonly ? {} : {
            bgcolor: "action.hover",
            "& .edit-icon": { opacity: 1 },
          },
        }}
      >
        {top ? (
          <>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Tooltip title={getCandidateLabel(top)} placement="top-start" enterDelay={600}>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: "0.74rem",
                    lineHeight: 1.35,
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    wordBreak: "break-word",
                  }}
                >
                  {getCandidateLabel(top)}
                </Typography>
              </Tooltip>
              <Box sx={{ mt: 0.25, display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                <MatchTypeChip
                  matchType={top.match_type}
                  matchPercent={top.match_percent}
                  manuallySelected={isManual(top)}
                  dense
                />
                {meta.udm && (
                  <Typography sx={{ color: "text.disabled", fontWeight: 600, fontSize: "0.64rem" }}>
                    UM: {meta.udm}
                  </Typography>
                )}
              </Box>
              {meta.source_file && (
                <Tooltip title={`Fonte: ${meta.source_file}`}>
                  <Typography
                    noWrap
                    sx={{ display: "block", mt: 0.15, color: "text.disabled", fontSize: "0.62rem", maxWidth: "100%" }}
                  >
                    Fonte: {meta.source_file}
                  </Typography>
                </Tooltip>
              )}
            </Box>
            {!readonly && (
              <EditIcon
                className="edit-icon"
                sx={{ fontSize: "0.85rem", color: "text.disabled", opacity: 0, transition: "opacity 0.15s", flexShrink: 0, mt: 0.25 }}
              />
            )}
          </>
        ) : matchError ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {statusLabel(
              <ErrorOutlineIcon sx={{ fontSize: "0.9rem", flexShrink: 0 }} />,
              "Errore match",
              "error.main",
              `Errore durante il matching: ${matchError} — clicca per cercare manualmente`,
            )}
            {!readonly && preventivoId && rowId && (
              <Tooltip title="Riprova match automatico">
                <IconButton size="small" onClick={handleRematch} disabled={rematching} sx={{ p: 0.25 }}>
                  <ReplayIcon sx={{ fontSize: "0.9rem" }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ) : isNp ? (
          // NP rows are excluded from auto-matching by design. After a manual
          // pick + deselect, candidates may still be in the list — still show
          // the NP label, not "Nessun match valido" / "Nessun match".
          statusLabel(
            <HelpOutlineIcon sx={{ fontSize: "0.9rem" }} />,
            "NP — match manuale",
            "#9E8B00",
            "Codice Nuovo Prezzo (NP.*): escluso dal matching automatico, richiede sempre un abbinamento manuale — clicca per cercare",
          )
        ) : deselected ? (
          statusLabel(
            <HelpOutlineIcon sx={{ fontSize: "0.9rem" }} />,
            "Nessun match",
            "#E65100",
            "Match rimosso manualmente — clicca per sceglierne uno tra i suggerimenti o cercare nel catalogo",
          )
        ) : allExcluded ? (
          statusLabel(
            <HelpOutlineIcon sx={{ fontSize: "0.9rem" }} />,
            "Nessun match valido",
            "#E65100",
            "Il sistema ha analizzato il catalogo ma nessun articolo è compatibile con questa voce — clicca per cercare manualmente",
          )
        ) : (
          statusLabel(
            <HelpOutlineIcon sx={{ fontSize: "0.9rem" }} />,
            "Nessun match",
            "#E65100",
            "Nessun articolo trovato a catalogo per questa voce — clicca per cercare manualmente",
          )
        )}
      </Box>

      {popoverOpen && (
        <MatchPickerPopover
          anchorEl={cellRef.current}
          onClose={() => setPopoverOpen(false)}
          queryText={queryText}
          suggestions={candidates ?? []}
          loadingSuggestions={false}
          selectedUid={selectedUid}
          onChoose={handleChoose}
          onDeselect={onDeselectCandidate ? handleDeselect : undefined}
          preventivoCategoria={preventivoCategoria}
          disciplina={disciplina}
        />
      )}
    </>
  );
};
